import { signOut } from '@app/icare/client/auth/auth'
import { createORPCClient, ORPCError, onError } from '@orpc/client'
import { RPCLink as FetchRPCLink } from '@orpc/client/fetch'
import type { RouterClient } from '@orpc/server'
import { createTanstackQueryUtils } from '@orpc/tanstack-query'
import { appError, fromORPCError } from '@pkg/shared/errors'
import { logger } from '@pkg/shared/logger'
import { devServerUrl } from '@pkg/shared/utils/dev-server.utils'
import { type ZodError, z } from 'zod'
import type { Router } from '../api/api'

let baseUrl: string
if (typeof window === 'undefined') {
  // SSR only. The stage is read from sst's env injection instead of `import { Resource }
  // from 'sst'` because this module is shared with the client bundle.
  const app = process.env.SST_RESOURCE_App
  const stage = app ? (JSON.parse(app) as { stage?: string }).stage : undefined
  baseUrl = `${devServerUrl(stage ?? '', process.env.PORT)}/api/rpc`
} else {
  const { protocol, host } = window.location
  baseUrl = `${protocol}//${host}`
}

const link = new FetchRPCLink({
  url: `${baseUrl}/api/rpc`,
  interceptors: [
    onError(error => {
      if (error instanceof ORPCError) {
        if (error.code === 'BAD_REQUEST' && (error.cause as ZodError)?.issues) {
          // If you only use Zod you can safely cast to ZodIssue[]
          const zodError = new z.ZodError((error.cause as ZodError)?.issues as z.core.$ZodIssue[])

          throw appError('INPUT_VALIDATION_FAILED', {
            status: 422,
            message: z.prettifyError(zodError),
            cause: error.cause,
          })
        }

        if (error.code === 'INTERNAL_SERVER_ERROR' && error.cause) {
          throw appError('OUTPUT_VALIDATION_FAILED', {
            cause: error.cause,
          })
        }
        if (error.code === 'UNAUTHORIZED') {
          void signOut().then(() => {
            logger.info(`Logged out after server send unauthorized`)
          })
          return
        }

        throw fromORPCError(error)
      } else {
        throw error
      }
    }),
  ],
})

export const apiClient: RouterClient<Router> = createORPCClient(link)

export const query = createTanstackQueryUtils(apiClient, {
  path: ['api'],
})
